import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { productService, orderService } from "./services/api";
import { Search, Plus, Minus, User, UserCheck, DollarSign } from "lucide-react";

export default function AdminCreateOrder() {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isGuest, setIsGuest] = useState(true);
  const [customerName, setCustomerName] = useState("");
  const [tipOption, setTipOption] = useState("none"); // none, 10, 15, 20, custom
  const [customTip, setCustomTip] = useState(0);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productService.getAll({ limit: 100 });
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item => 
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const updateQuantity = (id, delta) => {
    setCart(cart.map(item => {
      if (item.id === id) {
        const newQty = Math.max(0, item.quantity + delta);
        return newQty === 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Tax logic (Simplified for POS - assuming default Summerville/Dorchester 7% + 2% Hosp for food)
  // In a real app, we'd call a tax calc endpoint, but for POS we usually assume local shop rate.
  const salesTaxRate = 0.07; // 7% (Dorchester)
  const salesTax = subtotal * salesTaxRate;
  
  // Hospitality Tax (2% on food/bev)
  const hospitalityTax = cart.reduce((sum, item) => {
    const isFoodOrBev = true; // For now assume POS items are food/bev, or add category check
    return isFoodOrBev ? sum + (item.price * item.quantity * 0.02) : sum;
  }, 0);

  const calculatedTip = tipOption === "custom" 
    ? Number(customTip) 
    : tipOption === "none" 
      ? 0 
      : subtotal * (Number(tipOption) / 100);

  const total = subtotal + salesTax + hospitalityTax + calculatedTip;

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;
    setLoading(true);

    try {
      await orderService.create({
        userEmail: isGuest ? "guest@local.POS" : "customer@local.POS",
        customerName: isGuest ? "Guest" : customerName,
        total: total,
        subtotal: subtotal,
        taxAmount: salesTax + hospitalityTax,
        tipAmount: calculatedTip,
        items: cart,
        paymentMethod: "cash",
        orderType: "local",
        shippingAddress: { city: "Summerville", state: "SC", zip: "29485" }, // Default local
        taxBreakdown: [
          { name: "SC Sales Tax", rate: "7%", amount: Math.round(salesTax * 100) },
          { name: "Hospitality Tax", rate: "2%", amount: Math.round(hospitalityTax * 100) }
        ]
      });
      toast.success("Order placed successfully!");
      setCart([]);
      setCustomerName("");
      setIsGuest(true);
      setTipOption("none");
    } catch (error) {
      toast.error("Failed to place order: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-4">
      {/* Product Selection */}
      <div className="lg:col-span-2 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-raya-text-tertiary" size={20} />
          <input 
            type="text" 
            placeholder="Search products..."
            className="w-full pl-10 pr-4 py-3 bg-raya-card border border-raya-border rounded-xl focus:ring-2 focus:ring-raya-accent outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 h-[600px] overflow-y-auto pr-2">
          {filteredProducts.map(product => (
            <button 
              key={product.id}
              onClick={() => addToCart(product)}
              className="bg-raya-card p-4 rounded-xl border border-raya-border hover:border-raya-accent transition-all text-left group active:scale-95"
            >
              <div className="font-bold text-raya-text-primary group-hover:text-raya-accent">{product.name}</div>
              <div className="text-raya-accent font-mono text-sm">${Number(product.price).toFixed(2)}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Cart & Checkout */}
      <div className="bg-raya-card rounded-2xl border border-raya-border p-6 h-fit sticky top-24">
        <h3 className="text-xl font-black mb-6 flex items-center gap-2">
          < DollarSign className="text-raya-accent" />
          Current Order
        </h3>

        {/* Customer Mode */}
        <div className="flex bg-raya-bg-off p-1 rounded-lg mb-6">
          <button 
            onClick={() => setIsGuest(true)}
            className={`flex-1 py-2 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition ${isGuest ? 'bg-black text-white' : 'text-raya-text-tertiary'}`}
          >
            <User size={16} /> Guest
          </button>
          <button 
            onClick={() => setIsGuest(false)}
            className={`flex-1 py-2 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition ${!isGuest ? 'bg-black text-white' : 'text-raya-text-tertiary'}`}
          >
            <UserCheck size={16} /> Named
          </button>
        </div>

        {!isGuest && (
          <input 
            type="text" 
            placeholder="Enter customer name..."
            className="w-full px-4 py-2 mb-6 bg-raya-bg border border-raya-border rounded-lg outline-none focus:border-raya-accent"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />
        )}

        {/* Cart Items */}
        <div className="space-y-4 mb-8 max-h-60 overflow-y-auto">
          {cart.length === 0 ? (
            <div className="text-center py-8 text-raya-text-tertiary italic text-sm">Cart is empty</div>
          ) : (
            cart.map(item => (
              <div key={item.id} className="flex justify-between items-center text-sm">
                <div className="flex-1">
                  <div className="font-bold text-raya-text-primary">{item.name}</div>
                  <div className="text-xs text-raya-text-tertiary">${Number(item.price).toFixed(2)} / ea</div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => updateQuantity(item.id, -1)} className="p-1 hover:text-red-500 transition"><Minus size={14}/></button>
                  <span className="font-mono font-bold w-4 text-center">{item.quantity}</span>
                  <button onClick={() => addToCart(item)} className="p-1 hover:text-raya-accent transition"><Plus size={14}/></button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Tips Selection */}
        <div className="mb-6">
          <label className="text-xs font-black uppercase tracking-widest text-raya-text-tertiary mb-3 block">Add Tip</label>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {['10', '15', '20'].map(percent => (
              <button 
                key={percent}
                onClick={() => setTipOption(percent)}
                className={`py-2 rounded-lg text-xs font-bold border transition ${tipOption === percent ? 'bg-raya-accent border-raya-accent text-black' : 'border-raya-border text-raya-text-secondary hover:border-raya-accent'}`}
              >
                {percent}%
              </button>
            ))}
            <button 
              onClick={() => setTipOption('custom')}
              className={`py-2 rounded-lg text-xs font-bold border transition ${tipOption === 'custom' ? 'bg-raya-accent border-raya-accent text-black' : 'border-raya-border text-raya-text-secondary hover:border-raya-accent'}`}
            >
              Custom
            </button>
          </div>
          {tipOption === 'custom' && (
            <input 
              type="number" 
              placeholder="$ Amount"
              className="w-full px-4 py-2 bg-raya-bg border border-raya-border rounded-lg outline-none focus:border-raya-accent text-sm"
              value={customTip}
              onChange={(e) => setCustomTip(e.target.value)}
            />
          )}
          {tipOption !== 'none' && tipOption !== 'custom' && (
             <div className="text-right text-xs text-raya-text-tertiary mt-1">Calculated: ${calculatedTip.toFixed(2)}</div>
          )}
        </div>

        {/* Totals */}
        <div className="space-y-2 pt-4 border-t border-raya-border mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-raya-text-tertiary">Subtotal</span>
            <span className="font-mono">${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-raya-text-tertiary">Tax (Inc. Hosp)</span>
            <span className="font-mono">${(salesTax + hospitalityTax).toFixed(2)}</span>
          </div>
          {calculatedTip > 0 && (
            <div className="flex justify-between text-sm font-bold text-raya-accent">
              <span>Tip</span>
              <span className="font-mono">${calculatedTip.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-black pt-2 text-raya-text-primary">
            <span>Total</span>
            <span className="font-mono">${total.toFixed(2)}</span>
          </div>
        </div>

        <button 
          onClick={handlePlaceOrder}
          disabled={cart.length === 0 || loading}
          className="w-full bg-black text-white py-4 rounded-xl font-black hover:scale-[1.02] active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        >
          {loading ? 'Processing...' : 'Complete Cash Order'}
        </button>
      </div>
    </div>
  );
}
